import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Controller,
  UseFormReturn,
  useFieldArray,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import { CircleAlert, RefreshCw } from 'lucide-react';

import { Checkbox } from 'components/Checkbox';
import { Label } from 'components/Label';
import RequiredHint from 'components/RequiredHint';
import { BaseOptionType, Select } from 'components/Select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from 'components/Tooltip';
import { Input } from 'components/input/Input';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Flex, Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import {
  type EquipmentMappingItem,
  type LookupItem,
} from 'lib/api/getEquipmentLookupsGlobalTranz';
import {
  EQUIPMENT_PLACEHOLDER,
  EQUIPMENT_REQUIRED_MESSAGE,
  validateEquipmentRequired,
} from 'pages/QuoteView/LoadBuilding/utils/suggestedEquipment';
import { NormalizedLoad, createNewCommodity } from 'types/Load';
import { Unit } from 'types/Load';
import { MaybeUndef, Undef } from 'types/UtilityTypes';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { validateRequiredSelectMembership } from 'utils/formValidation';

import {
  GLOBALTRANZ_STATUS,
  getGlobalTranzStatusTransitionOptions,
  globalTranzServiceOptions,
  globalTranzShipmentTypes,
} from './constants';

// Display field IDs that should be excluded (accessorials are in separate section)
const EXCLUDED_DISPLAY_FIELD_IDS = [7, 8] as const; // LTL Accessorial (7), TL Accessorial (8)

// Display field IDs that need special handling
const DISPLAY_FIELD_IDS = {
  EQUIPMENT_LENGTH: 0,
  MIN_TEMP: 5,
  MAX_TEMP: 6,
} as const;

const DEFAULT_VAN_EQUIPMENT_LENGTH_FEET = 53;

// Type for a display field that should be shown
type DisplayFieldToShow = {
  id: number;
  displayText: string;
  hasValues: boolean; // Whether this field has dropdown values (like Equipment Length)
};

// Helper function to get display fields that should be shown based on equipment mapping
// Uses the equipment mapping to determine which fields to show dynamically
const getDisplayFieldsToShow = (
  equipmentType: Undef<string>,
  equipmentTypes: LookupItem[],
  equipmentMapping: EquipmentMappingItem[],
  displayFields: LookupItem[]
): DisplayFieldToShow[] => {
  if (
    !equipmentType ||
    !equipmentTypes.length ||
    !equipmentMapping.length ||
    !displayFields.length
  ) {
    return [];
  }

  // Find equipment type ID from displayText
  const equipmentTypeItem = equipmentTypes.find(
    (type) => type.displayText === equipmentType
  );
  if (!equipmentTypeItem) {
    return [];
  }

  // Find the equipment type in the mapping
  const mappingItem = equipmentMapping.find(
    (item) => item.id === equipmentTypeItem.id
  );
  if (!mappingItem || !mappingItem.displayFields) {
    return [];
  }

  // Get all display fields that exist in the mapping (except accessorials)
  const fieldsToShow: DisplayFieldToShow[] = [];

  for (const displayField of mappingItem.displayFields) {
    // Skip if displayField is invalid
    if (!displayField || !displayField.id) {
      continue;
    }

    // Skip accessorial fields (they're in a separate section)
    if (EXCLUDED_DISPLAY_FIELD_IDS.includes(displayField.id as any)) {
      continue;
    }

    // Get the display text from DisplayFields API
    const displayFieldInfo = displayFields.find(
      (df) => df.id === displayField.id
    );
    if (displayFieldInfo) {
      // Equipment Length (id: 0) uses a separate API for values, so always include it if it exists in mapping
      // For other fields, check if they have values
      const isEquipmentLength =
        displayField.id === DISPLAY_FIELD_IDS.EQUIPMENT_LENGTH;
      const hasValues = isEquipmentLength
        ? true // Equipment length always has values from separate API
        : displayField.values &&
          Array.isArray(displayField.values) &&
          displayField.values.length > 0;

      fieldsToShow.push({
        id: displayField.id,
        displayText: displayFieldInfo.displayText,
        hasValues,
      });
    }
  }

  return fieldsToShow;
};

const getDefaultEquipmentLength = (
  lengths: LookupItem[]
): Undef<LookupItem> => {
  const defaultLength = lengths.find((length) => {
    const match = length.displayText.match(/\d+/);
    return match
      ? Number(match[0]) === DEFAULT_VAN_EQUIPMENT_LENGTH_FEET
      : false;
  });
  if (defaultLength) {
    return defaultLength;
  }
  return lengths.length === 1 ? lengths[0] : undefined;
};

export function SpecificationsForm({
  formMethods,
  isCreateMode = false,
  tmsID: _tmsID,
  equipmentTypes: providedEquipmentTypes,
  equipmentMapping: providedEquipmentMapping,
  equipmentLengths: providedEquipmentLengths,
  displayFields: providedDisplayFields,
  onBootstrapRefresh,
  isRefreshingBootstrap = false,
  isLoadingBootstrap = false,
}: {
  formMethods: UseFormReturn<NormalizedLoad>;
  isCreateMode?: boolean;
  tmsID?: number;
  equipmentTypes?: LookupItem[];
  equipmentMapping?: EquipmentMappingItem[];
  equipmentLengths?: LookupItem[];
  displayFields?: LookupItem[];
  onBootstrapRefresh?: () => void;
  isRefreshingBootstrap?: boolean;
  isLoadingBootstrap?: boolean;
}) {
  const {
    control,
    formState: { errors },
    setValue,
    getValues,
    watch,
    trigger,
  } = formMethods;

  const modes = [
    'TL',
    'LTL',
    'Drayage',
    'Ocean',
    'Air',
    'Rail',
    'Parcel',
    'Intermodal',
  ];

  const {
    currentState: { clickedSuggestion },
  } = useContext(SidebarStateContext);

  const [tempDisplayValue, setTempDisplayValue] = useState<string>('');
  const [maxTempDisplayValue, setMaxTempDisplayValue] = useState<string>('');
  const [isTempInputFocused, setIsTempInputFocused] = useState<boolean>(false);
  const [isMaxTempInputFocused, setIsMaxTempInputFocused] =
    useState<boolean>(false);

  // Equipment lookup data - initialize from props when available (avoids empty flash on remount)
  const [equipmentTypes, setEquipmentTypes] = useState<LookupItem[]>(
    providedEquipmentTypes || []
  );
  const [equipmentLengths, setEquipmentLengths] = useState<LookupItem[]>(
    providedEquipmentLengths || []
  );
  const [equipmentMapping, setEquipmentMapping] = useState<
    EquipmentMappingItem[]
  >(providedEquipmentMapping || []);
  const [displayFields, setDisplayFields] = useState<LookupItem[]>(
    providedDisplayFields || []
  );

  const formatDisplayTemp = (fahrenheit: MaybeUndef<number>) => {
    if (fahrenheit == null) return '';
    return fahrenheit.toString();
  };

  const parseInputToFahrenheit = (value: string) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return null;
    return numValue;
  };

  // Update local state when provided props change
  useEffect(() => {
    if (providedEquipmentTypes) {
      setEquipmentTypes(providedEquipmentTypes);
    }
  }, [providedEquipmentTypes]);

  const isEquipmentOptionsLoading = isLoadingBootstrap || isRefreshingBootstrap;

  // After bootstrap settles, re-check membership only when a value is already
  // present (typical AI hydrate). Do not trigger on an empty untouched create
  // form — that would show Required before submit.
  useEffect(() => {
    if (!isCreateMode || isEquipmentOptionsLoading) {
      return;
    }
    const current = String(
      getValues('specifications.transportType') ?? ''
    ).trim();
    if (!current) {
      return;
    }
    void trigger('specifications.transportType');
  }, [
    equipmentTypes,
    getValues,
    isCreateMode,
    isEquipmentOptionsLoading,
    trigger,
  ]);

  useEffect(() => {
    if (providedEquipmentMapping) {
      setEquipmentMapping(providedEquipmentMapping);
    }
  }, [providedEquipmentMapping]);

  useEffect(() => {
    if (providedEquipmentLengths) {
      setEquipmentLengths(providedEquipmentLengths);
    }
  }, [providedEquipmentLengths]);

  useEffect(() => {
    if (providedDisplayFields) {
      setDisplayFields(providedDisplayFields);
    }
  }, [providedDisplayFields]);

  const watchedTotalWeight = watch('specifications.totalWeight.val');
  const watchedWeightUnit = watch('specifications.totalWeight.unit');
  const watchedTransportType = useWatch({
    control,
    name: 'specifications.transportType',
  });
  const watchedTransportSize = useWatch({
    control,
    name: 'specifications.transportSize',
  });
  const watchedServiceType = useWatch({
    control,
    name: 'specifications.serviceType',
  });

  // Watch service field (Full, Partial, Volume)
  const watchedService = useWatch({
    control,
    name: 'specifications.service',
  });

  // Check if service is "Full" (value "1" or 1)
  const isFullService = String(watchedService) === '1';

  // Get available equipment lengths for selected equipment type
  const availableLengths = useMemo(() => {
    if (
      !watchedTransportType ||
      !equipmentMapping.length ||
      !equipmentLengths.length
    ) {
      return equipmentLengths;
    }

    // Find equipment type ID from displayText
    const equipmentType = equipmentTypes.find(
      (type) => type.displayText === watchedTransportType
    );
    if (!equipmentType) {
      return equipmentLengths;
    }

    // Find the equipment type in the mapping
    const mappingItem = equipmentMapping.find(
      (item) => item.id === equipmentType.id
    );
    if (!mappingItem || !mappingItem.displayFields) {
      return equipmentLengths;
    }

    // Look for displayField with id 0 (equipment length field)
    const lengthField = mappingItem.displayFields.find(
      (field) => field.id === 0
    );
    if (
      !lengthField ||
      !lengthField.values ||
      !Array.isArray(lengthField.values) ||
      lengthField.values.length === 0
    ) {
      // No length field means no lengths available (e.g., Power Only)
      return [];
    }

    // Extract length IDs from values
    const allowedLengthIDs = lengthField.values.map((value) => value.id);

    // Filter equipment lengths to only include allowed ones
    return equipmentLengths.filter((length) =>
      allowedLengthIDs.includes(length.id)
    );
  }, [
    watchedTransportType,
    equipmentTypes,
    equipmentLengths,
    equipmentMapping,
  ]);

  // Check if service type is LTL (any variant)
  const isLTLServiceType =
    watchedServiceType === 'LTL' ||
    watchedServiceType === 'LTL Manual' ||
    watchedServiceType === 'LTL Cost Only';

  // Check if service type is TL E-tender
  const isTLEtender = watchedServiceType === 'TL E-tender';

  // Check if service type is TL (not E-tender)
  const isTLOnly = watchedServiceType === 'TL';

  // Set service type to TL by default for create load
  useEffect(() => {
    if (!watchedServiceType) {
      setValue('specifications.serviceType', 'TL', {
        shouldDirty: false,
      });
    }
  }, [watchedServiceType, setValue]);

  // Check if service type is TL or TL E-tender (for shipment type field)
  const isTLServiceType =
    watchedServiceType === 'TL' || watchedServiceType === 'TL E-tender';

  // Shipment type options
  const shipmentTypeOptions = globalTranzShipmentTypes.map((type) => ({
    value: type,
    label: type,
  }));

  // Get display fields that should be shown based on selected equipment type and mapping
  const displayFieldsToShow = useMemo(() => {
    if (!isTLOnly) return [];
    return getDisplayFieldsToShow(
      watchedTransportType,
      equipmentTypes,
      equipmentMapping,
      displayFields
    );
  }, [
    watchedTransportType,
    equipmentTypes,
    equipmentMapping,
    displayFields,
    isTLOnly,
  ]);

  // Helper to check if a specific field should be shown
  const shouldShowField = (fieldId: number): boolean => {
    return displayFieldsToShow.some((field) => field.id === fieldId);
  };

  // For backward compatibility and special handling
  // Equipment length should be shown if:
  // 1. It's TL only
  // 2. Either it's in the displayFieldsToShow OR there are available lengths for this equipment type
  // (availableLengths is filtered based on equipment mapping, so if it has items, the equipment supports length)
  const showEquipmentLength =
    isTLOnly &&
    (shouldShowField(DISPLAY_FIELD_IDS.EQUIPMENT_LENGTH) ||
      availableLengths.length > 0);
  const showTemperatureFields =
    isTLOnly &&
    (shouldShowField(DISPLAY_FIELD_IDS.MIN_TEMP) ||
      shouldShowField(DISPLAY_FIELD_IDS.MAX_TEMP));

  useEffect(() => {
    if (watchedTotalWeight > 0 && !watchedWeightUnit) {
      setValue('specifications.totalWeight.unit', Unit.Pound, {
        shouldDirty: true,
      });
    }
  }, [watchedTotalWeight, watchedWeightUnit, setValue]);

  // Note: isRefrigerated is not used for GlobalTranz - temperature fields are determined by equipment mapping

  const watchedStatus = watch('status');
  const loadedStatusRef = useRef<Undef<string>>(undefined);
  if (!loadedStatusRef.current) {
    loadedStatusRef.current = watchedStatus || GLOBALTRANZ_STATUS.Open;
  }

  useEffect(() => {
    if (!isCreateMode && !watchedStatus) {
      setValue('status', GLOBALTRANZ_STATUS.Open, { shouldDirty: false });
    }
  }, [isCreateMode, watchedStatus, setValue]);

  const statusOptions = useMemo<BaseOptionType[]>(() => {
    const loadedStatus = loadedStatusRef.current || GLOBALTRANZ_STATUS.Open;

    const transitionOptions =
      getGlobalTranzStatusTransitionOptions(loadedStatus);
    const options: BaseOptionType[] = transitionOptions
      .filter((status) => status !== loadedStatus)
      .map((status) => ({
        value: status,
        label: status,
      }));

    options.unshift({
      value: loadedStatus,
      label: loadedStatus,
    });

    return options;
  }, []);

  const watchedMinTemp = watch('specifications.minTempFahrenheit');
  const watchedMaxTemp = watch('specifications.maxTempFahrenheit');

  useEffect(() => {
    if (!isTempInputFocused) {
      setTempDisplayValue(formatDisplayTemp(watchedMinTemp));
    }
  }, [watchedMinTemp, isTempInputFocused]);

  useEffect(() => {
    if (!isMaxTempInputFocused) {
      setMaxTempDisplayValue(formatDisplayTemp(watchedMaxTemp));
    }
  }, [watchedMaxTemp, isMaxTempInputFocused]);

  useEffect(() => {
    if (
      clickedSuggestion &&
      clickedSuggestion.pipeline === SuggestionPipelines.LoadBuilding
    ) {
      return;
    }

    setValue('specifications.minTempFahrenheit', null, { shouldDirty: true });
    setValue('specifications.maxTempFahrenheit', null, { shouldDirty: true });
  }, [setValue, watchedTransportType, clickedSuggestion]);

  // Set static values for TL E-tender when selected
  useEffect(() => {
    if (isTLEtender) {
      setValue('specifications.transportType', 'Van', {
        shouldDirty: true,
      });
      // Currency will be set to USD when the load is created in the backend
    }
  }, [isTLEtender, setValue]);

  useEffect(() => {
    if (
      !isCreateMode ||
      !isFullService ||
      !watchedTransportType ||
      !showEquipmentLength ||
      watchedTransportSize ||
      !availableLengths.length
    ) {
      return;
    }

    const defaultLength = getDefaultEquipmentLength(availableLengths);
    if (!defaultLength) {
      return;
    }

    setValue('specifications.transportSize', String(defaultLength.id), {
      shouldDirty: false,
      shouldValidate: true,
    });
  }, [
    availableLengths,
    isCreateMode,
    isFullService,
    setValue,
    showEquipmentLength,
    watchedTransportType,
    watchedTransportSize,
  ]);

  // Clear equipment length when equipment type changes (if new type doesn't support current length)
  useEffect(() => {
    if (!isFullService) {
      return;
    }

    if (
      !watchedTransportType ||
      !equipmentMapping.length ||
      !equipmentLengths.length
    ) {
      return;
    }

    const currentLength = watchedTransportSize;
    if (!currentLength) return;

    // Check if current length is still valid for the new equipment type
    const equipmentType = equipmentTypes.find(
      (type) => type.displayText === watchedTransportType
    );
    if (!equipmentType) return;

    const mappingItem = equipmentMapping.find(
      (item) => item.id === equipmentType.id
    );
    if (!mappingItem || !mappingItem.displayFields) return;

    const lengthField = mappingItem.displayFields.find(
      (field) => field.id === 0
    );
    if (
      !lengthField ||
      !lengthField.values ||
      !Array.isArray(lengthField.values) ||
      lengthField.values.length === 0
    ) {
      // No lengths available for this equipment type
      setValue('specifications.transportSize', '', {
        shouldDirty: true,
      });
      return;
    }

    // Allowed values for this equipment type: length IDs (e.g. 2 for "40 HC")
    const allowedLengthIds = new Set(
      lengthField.values.map((v) => String(v.id))
    );

    // Current value is equipment length ID (string); clear if not valid for this equipment type
    if (
      currentLength &&
      allowedLengthIds.size > 0 &&
      !allowedLengthIds.has(String(currentLength))
    ) {
      setValue('specifications.transportSize', '', {
        shouldDirty: true,
      });
    }
  }, [
    watchedTransportType,
    equipmentTypes,
    equipmentLengths,
    equipmentMapping,
    setValue,
    isFullService,
    watchedTransportSize,
  ]);

  // Clear stale equipmentDimensions when equipment type changes (e.g. L/W/H from Double Drop not applicable to Van)
  useEffect(() => {
    if (!watchedTransportType || !equipmentMapping.length) return;

    const equipmentType = equipmentTypes.find(
      (type) => type.displayText === watchedTransportType
    );
    if (!equipmentType) return;

    const mappingItem = equipmentMapping.find(
      (item) => item.id === equipmentType.id
    );
    if (!mappingItem?.displayFields?.length) return;

    const validDimensionFieldIds = new Set(
      mappingItem.displayFields
        .filter(
          (f) =>
            f.id !== DISPLAY_FIELD_IDS.EQUIPMENT_LENGTH &&
            f.id !== DISPLAY_FIELD_IDS.MIN_TEMP &&
            f.id !== DISPLAY_FIELD_IDS.MAX_TEMP
        )
        .map((f) => String(f.id))
    );

    const current = watch('specifications.equipmentDimensions') || {};
    const filtered: Record<string, string> = {};
    for (const [key, value] of Object.entries(current)) {
      if (validDimensionFieldIds.has(key) && value != null && value !== '') {
        filtered[key] = value;
      }
    }
    if (Object.keys(filtered).length !== Object.keys(current).length) {
      setValue('specifications.equipmentDimensions', filtered, {
        shouldDirty: true,
      });
    }
  }, [watchedTransportType, equipmentTypes, equipmentMapping, watch, setValue]);

  const { fields: commodities, append: appendCommodity } = useFieldArray({
    control,
    name: 'commodities',
  });

  useEffect(() => {
    if (commodities.length === 0) {
      appendCommodity(createNewCommodity({ dimensionUnit: 'inches' }));
    }
  }, [commodities.length, appendCommodity]);

  return (
    <Grid cols='4' gap='sm' className='mx-0 w-full'>
      {!isCreateMode && (
        <div className='col-span-4'>
          <Label hideAIHint={true} name='status'>
            Status
          </Label>
          <Controller
            name='status'
            control={control}
            render={({ field }) => (
              <Select
                data-testid='gtz-load-status-select'
                showSearch
                className='text-neutral-500'
                placeholder='Choose'
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  String(optionA?.label)
                    .toLowerCase()
                    .localeCompare(String(optionB?.label).toLowerCase())
                }
                onChange={(value) => field.onChange(value)}
                value={field.value || undefined}
                disabled={watchedStatus === GLOBALTRANZ_STATUS.Canceled}
                options={statusOptions}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name='status'
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
      )}

      {/* Mode field - hidden for TL */}
      {!isTLOnly && (
        <div className='col-span-4'>
          <Label hideAIHint={true} name={'mode'} required={true}>
            Mode
          </Label>
          <Controller
            name='mode'
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select
                showSearch
                className='text-neutral-500'
                placeholder={'Choose'}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  String(optionA?.label)
                    .toLowerCase()
                    .localeCompare(String(optionB?.label).toLowerCase())
                }
                onChange={(value) => field.onChange(value)}
                value={field.value}
                options={modes?.map((mode) => ({
                  value: mode,
                  label: mode,
                }))}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={'mode'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
      )}

      {/* Service Type - for TL only, show only TL option (disabled) */}
      {isTLOnly && (
        <div className='col-span-4'>
          <Label
            name={'serviceType'}
            hideAIHint={
              !clickedSuggestion ||
              clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
            }
            required={true}
          >
            Service Type
          </Label>
          <Controller
            name='specifications.serviceType'
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select
                showSearch
                className='text-neutral-500'
                placeholder={'Choose'}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  String(optionA?.label)
                    .toLowerCase()
                    .localeCompare(String(optionB?.label).toLowerCase())
                }
                disabled={true}
                value={field.value || 'TL'}
                options={[{ value: 'TL', label: 'TL' }]}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={'specifications.serviceType'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
      )}

      {/* Shipment Type - only shown for TL and TL E-tender */}
      {isTLServiceType && (
        <div className='col-span-4'>
          <Label
            name={'specifications.shipmentType'}
            hideAIHint={
              !clickedSuggestion ||
              clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
            }
            required={true}
          >
            Shipment Type
          </Label>
          <Controller
            name='specifications.shipmentType'
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }) => (
              <Select
                showSearch
                className='text-neutral-500'
                placeholder={'Choose'}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  String(optionA?.label)
                    .toLowerCase()
                    .localeCompare(String(optionB?.label).toLowerCase())
                }
                onChange={(value) => field.onChange(value)}
                value={field.value}
                options={shipmentTypeOptions}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name={'specifications.shipmentType'}
            render={({ message }: { message: string }) => (
              <Typography variant='body-xs' className='text-error-500'>
                {message}
              </Typography>
            )}
          />
        </div>
      )}

      {/* Service dropdown - only shown for TL */}
      {isTLOnly && (
        <div className='col-span-4'>
          <Label
            name={'specifications.service'}
            hideAIHint={
              !clickedSuggestion ||
              clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
            }
            required={false}
          >
            Service
          </Label>
          <Controller
            name='specifications.service'
            control={control}
            render={({ field }) => (
              <Select
                showSearch
                className='text-neutral-500'
                placeholder={'Choose'}
                filterOption={(input: string, option: Undef<BaseOptionType>) =>
                  String(option?.label)
                    .toLocaleLowerCase()
                    .includes(input.toLocaleLowerCase())
                }
                filterSort={(
                  optionA: BaseOptionType,
                  optionB: BaseOptionType
                ) =>
                  String(optionA?.label)
                    .toLowerCase()
                    .localeCompare(String(optionB?.label).toLowerCase())
                }
                onChange={(value) => field.onChange(value)}
                value={
                  field.value !== undefined ? String(field.value) : undefined
                }
                options={globalTranzServiceOptions.map((option) => ({
                  value: String(option.value),
                  label: option.label,
                }))}
              />
            )}
          />
        </div>
      )}

      {/* Currency - read-only display for user info only (not sent to BE) */}
      <div className='col-span-4'>
        <Label name='currency' required={false}>
          Currency
        </Label>
        <Input
          value='USD'
          disabled
          readOnly
          className='mt-1'
          aria-label='Currency (display only)'
        />
      </div>

      {/* Equipment details - hidden for LTL service types */}
      {!isLTLServiceType && (
        <>
          <div className='col-span-4'>
            <Label
              name={'specifications.transportType'}
              hideAIHint={
                !clickedSuggestion ||
                clickedSuggestion.pipeline !== SuggestionPipelines.LoadBuilding
              }
              required={false}
              className='inline-flex items-center gap-1'
            >
              <div>
                Equipment
                {!isTLEtender && <RequiredHint />}
              </div>
              {onBootstrapRefresh && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger
                      type='button'
                      className='flex items-center justify-center text-neutral-400 hover:text-neutral-600 cursor-help'
                    >
                      <CircleAlert className='h-3.5 w-3.5' />
                    </TooltipTrigger>
                    <TooltipContent
                      side='right'
                      align='center'
                      sideOffset={8}
                      className='max-w-[220px] whitespace-normal wrap-break-word text-[11px] leading-snug'
                    >
                      Need the latest options? Click refresh to update this
                      equipment, its length choices, and related accessorial
                      options in this form.
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </Label>
            <Flex align='center' gap='sm'>
              <div className='flex-1 min-w-0'>
                <Controller
                  name='specifications.transportType'
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (isTLEtender) {
                        return true;
                      }
                      if (!isCreateMode) {
                        return validateEquipmentRequired(value);
                      }

                      return validateRequiredSelectMembership(
                        value,
                        equipmentTypes.map((type) =>
                          String(type.displayText ?? '').trim()
                        ),
                        {
                          isLoading: isEquipmentOptionsLoading,
                          message: EQUIPMENT_REQUIRED_MESSAGE,
                        }
                      );
                    },
                  }}
                  render={({ field }) => {
                    const optionValues = new Set(
                      equipmentTypes.map((type) =>
                        String(type.displayText ?? '').trim()
                      )
                    );
                    const selected = String(field.value ?? '').trim();
                    let selectValue: Undef<string>;
                    if (selected) {
                      if (!isCreateMode || optionValues.has(selected)) {
                        selectValue = selected;
                      }
                    }
                    return (
                      <Select
                        showSearch
                        className='text-neutral-500 w-full'
                        placeholder={EQUIPMENT_PLACEHOLDER}
                        disabled={isTLEtender}
                        filterOption={(
                          input: string,
                          option: Undef<BaseOptionType>
                        ) =>
                          String(option?.label)
                            .toLocaleLowerCase()
                            .includes(input.toLocaleLowerCase())
                        }
                        filterSort={(
                          optionA: BaseOptionType,
                          optionB: BaseOptionType
                        ) =>
                          String(optionA?.label)
                            .toLowerCase()
                            .localeCompare(String(optionB?.label).toLowerCase())
                        }
                        onChange={field.onChange}
                        value={selectValue}
                        options={
                          equipmentTypes.length > 0
                            ? equipmentTypes.map((type) => ({
                                value: type.displayText,
                                label: type.displayText,
                              }))
                            : []
                        }
                      />
                    );
                  }}
                />
              </div>
              {onBootstrapRefresh && (
                <button
                  type='button'
                  title='Refresh reference data from GlobalTranz'
                  onClick={onBootstrapRefresh}
                  disabled={isRefreshingBootstrap || isTLEtender}
                  className='shrink-0 py-2 px-1 hover:text-info-600 rounded-full transition-colors disabled:cursor-not-allowed disabled:text-neutral-300'
                >
                  {isRefreshingBootstrap ? (
                    <ButtonLoader className='mr-0' />
                  ) : (
                    <RefreshCw className='h-4 w-4' />
                  )}
                </button>
              )}
            </Flex>
            <ErrorMessage
              errors={errors}
              name={'specifications.transportType'}
              render={({ message }: { message: string }) => (
                <Typography variant='body-xs' className='text-error-500'>
                  {message}
                </Typography>
              )}
            />
          </div>

          {/* Equipment Length - shown based on equipment type for TL only */}
          {/* Dropdown for Full service, text input for Partial/Volume */}
          {showEquipmentLength && (
            <div className='col-span-4'>
              <Label name={'specifications.transportSize'} required={true}>
                Equipment Length
              </Label>
              <Controller
                name='specifications.transportSize'
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => {
                  if (isFullService) {
                    const hasMatchingLength = availableLengths.some(
                      (length) => String(length.id) === String(field.value)
                    );

                    // Create mode: no transportSize from BE, always show dropdown.
                    // View/edit: use Select when current value matches a known length ID;
                    // otherwise (e.g. legacy raw feet), fall back to numeric input below.
                    if (field.value === '' || hasMatchingLength) {
                      return (
                        <Select
                          showSearch
                          className='text-neutral-500'
                          placeholder={'Choose'}
                          filterOption={(
                            input: string,
                            option: Undef<BaseOptionType>
                          ) =>
                            String(option?.label)
                              .toLocaleLowerCase()
                              .includes(input.toLocaleLowerCase())
                          }
                          filterSort={(
                            optionA: BaseOptionType,
                            optionB: BaseOptionType
                          ) =>
                            String(optionA?.label)
                              .toLowerCase()
                              .localeCompare(
                                String(optionB?.label).toLowerCase()
                              )
                          }
                          onChange={field.onChange}
                          value={field.value}
                          options={availableLengths.map((length) => ({
                            value: String(length.id), // Send equipment length ID (e.g. 2 for "40 HC") to match GTZ CreateOrder API
                            label: length.displayText,
                          }))}
                        />
                      );
                    }
                  }

                  return (
                    <Input
                      type='number'
                      min={0}
                      step={1}
                      placeholder='Enter equipment length (ft)'
                      className='text-neutral-500'
                      value={field.value ?? ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === '') {
                          field.onChange('');
                          return;
                        }
                        const n = parseInt(raw, 10);
                        if (!Number.isNaN(n) && n >= 0) {
                          field.onChange(String(n));
                        }
                      }}
                    />
                  );
                }}
              />
              <ErrorMessage
                errors={errors}
                name={'specifications.transportSize'}
                render={({ message }: { message: string }) => (
                  <Typography variant='body-xs' className='text-error-500'>
                    {message}
                  </Typography>
                )}
              />
            </div>
          )}
        </>
      )}

      {/* Temperature fields - shown for temp controlled equipment in TL only (based on equipment mapping) */}
      {showTemperatureFields && (
        // TL with temp controlled equipment - show separate Min and Max Temp fields
        <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
          {shouldShowField(DISPLAY_FIELD_IDS.MIN_TEMP) && (
            <div className='col-span-1'>
              <Label name='specifications.minTempFahrenheit' required={true}>
                Min Temp
              </Label>
              <Controller
                name='specifications.minTempFahrenheit'
                control={control}
                rules={{
                  required: 'Minimum temperature is required',
                }}
                render={({ field }) => (
                  <Flex
                    direction='row'
                    align='center'
                    gap='sm'
                    className='w-full'
                  >
                    <Input
                      {...field}
                      placeholder='e.g. 32'
                      value={tempDisplayValue}
                      onChange={(e) => setTempDisplayValue(e.target.value)}
                      onFocus={() => setIsTempInputFocused(true)}
                      onBlur={() => {
                        setIsTempInputFocused(false);
                        const fahrenheit =
                          parseInputToFahrenheit(tempDisplayValue);
                        field.onChange(fahrenheit);
                      }}
                    />
                  </Flex>
                )}
              />
              <ErrorMessage
                errors={errors}
                name={'specifications.minTempFahrenheit'}
                render={({ message }: { message: string }) => (
                  <Typography variant='body-xs' className='text-error-500'>
                    {message}
                  </Typography>
                )}
              />
            </div>
          )}
          {shouldShowField(DISPLAY_FIELD_IDS.MAX_TEMP) && (
            <div className='col-span-1'>
              <Label name='specifications.maxTempFahrenheit' required={true}>
                Max Temp
              </Label>
              <Controller
                name='specifications.maxTempFahrenheit'
                control={control}
                rules={{
                  required: 'Maximum temperature is required',
                }}
                render={({ field }) => (
                  <Flex
                    direction='row'
                    align='center'
                    gap='sm'
                    className='w-full'
                  >
                    <Input
                      {...field}
                      placeholder='e.g. 38'
                      value={maxTempDisplayValue}
                      onChange={(e) => setMaxTempDisplayValue(e.target.value)}
                      onFocus={() => setIsMaxTempInputFocused(true)}
                      onBlur={() => {
                        setIsMaxTempInputFocused(false);
                        const fahrenheit =
                          parseInputToFahrenheit(maxTempDisplayValue);
                        field.onChange(fahrenheit);
                      }}
                    />
                  </Flex>
                )}
              />
              <ErrorMessage
                errors={errors}
                name={'specifications.maxTempFahrenheit'}
                render={({ message }: { message: string }) => (
                  <Typography variant='body-xs' className='text-error-500'>
                    {message}
                  </Typography>
                )}
              />
            </div>
          )}
        </Grid>
      )}

      {/* Other dynamic display fields (L, W, H, etc.) - shown based on equipment mapping */}
      {/* Dropdown for Full service (if field has values), text input for Partial/Volume or if no values */}
      {isTLOnly && displayFieldsToShow.length > 0 && (
        <Grid cols='2' gap='sm' className='mx-0 w-full col-span-4'>
          {displayFieldsToShow
            .filter(
              (field) =>
                // Exclude fields we're already handling specially
                field.id !== DISPLAY_FIELD_IDS.EQUIPMENT_LENGTH &&
                field.id !== DISPLAY_FIELD_IDS.MIN_TEMP &&
                field.id !== DISPLAY_FIELD_IDS.MAX_TEMP
            )
            .map((field) => {
              // Field IDs: 2 = L (in), 3 = W (in), 4 = H (in), etc.
              const fieldName =
                `specifications.equipmentDimensions.${field.id}` as any;

              // Get values for dropdown if available
              const getFieldValues = (): BaseOptionType[] => {
                if (
                  !field.hasValues ||
                  !watchedTransportType ||
                  !equipmentMapping.length
                ) {
                  return [];
                }

                // Find equipment type
                const equipmentType = equipmentTypes.find(
                  (type) => type.displayText === watchedTransportType
                );
                if (!equipmentType) return [];

                // Find mapping item
                const mappingItem = equipmentMapping.find(
                  (item) => item.id === equipmentType.id
                );
                if (!mappingItem || !mappingItem.displayFields) return [];

                // Find the display field
                const displayField = mappingItem.displayFields.find(
                  (df) => df.id === field.id
                );
                if (
                  !displayField ||
                  !displayField.values ||
                  !Array.isArray(displayField.values)
                ) {
                  return [];
                }

                // Extract values - the values array contains EquipmentMappingValue objects
                // TODO: Get display text from API response structure
                return [];
              };

              const fieldOptions = getFieldValues();
              const shouldShowDropdown =
                isFullService && fieldOptions.length > 0;

              return (
                <div key={field.id} className='col-span-1'>
                  <Label name={fieldName} required={true}>
                    {field.displayText}
                  </Label>
                  <Controller
                    name={fieldName}
                    control={control}
                    rules={{ required: 'Required' }}
                    render={({ field: formField }) =>
                      shouldShowDropdown ? (
                        <Select
                          showSearch
                          className='text-neutral-500'
                          placeholder='Choose'
                          filterOption={(
                            input: string,
                            option: Undef<BaseOptionType>
                          ) =>
                            String(option?.label)
                              .toLocaleLowerCase()
                              .includes(input.toLocaleLowerCase())
                          }
                          onChange={(value) => formField.onChange(value)}
                          value={formField.value || undefined}
                          options={fieldOptions}
                        />
                      ) : (
                        <Input
                          {...formField}
                          type='text'
                          placeholder='Enter value'
                          className='text-neutral-500'
                          value={formField.value || ''}
                          onChange={(e) => formField.onChange(e.target.value)}
                        />
                      )
                    }
                  />
                  <ErrorMessage
                    errors={errors}
                    name={fieldName}
                    render={({ message }: { message: string }) => (
                      <Typography variant='body-xs' className='text-error-500'>
                        {message}
                      </Typography>
                    )}
                  />
                </div>
              );
            })}
        </Grid>
      )}

      {/* Can Get checkbox - only shown for TL */}
      {isTLOnly && (
        <div className='col-span-4'>
          <Controller
            name='specifications.canGet'
            control={control}
            render={({ field }) => (
              <Flex align='center' gap='sm'>
                <Checkbox
                  onCheckedChange={(checked) => {
                    field.onChange(checked === true);
                  }}
                  checked={field.value ? true : false}
                />
                <label
                  htmlFor='specifications.canGet'
                  className='leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 text-neutral-500'
                >
                  Can Get
                </label>
              </Flex>
            )}
          />
        </div>
      )}

      {/* Description field - hidden for TL */}
      {!isTLOnly && (
        <div className='col-span-4'>
          <RHFTextInput
            name='specifications.commodities'
            label='Description'
            required={false}
          />
        </div>
      )}
    </Grid>
  );
}
